import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import { api } from '../api/client';
import {
    getMyKeys,
    encryptMessage,
    decryptMessage,
    KeyPair,
    STORAGE_KEY,
    encodeBase64,
    generateSalt,
    deriveKeyFromPassword,
    encryptPrivateKey,
    decryptPrivateKey,
    generateEscrowKey
} from '../utils/crypto';
import nacl from 'tweetnacl';
import type {
    User,
    Department,
    Channel,
    DirectMessage,
    Topic,
    Ticket,
    Message,
    ActiveView,
    ThreadData,
    CreateTicketData,
    UpdateTicketData,
    CreateDepartmentData,
    UpdateUserData,
    Role,
    FileRecord,
    GlobalSettings,
    ModuleId,
    Project,
    CreateProjectData,
    ProjectBulkChanges
} from '../types';

interface AppContextType {
    // Data
    currentUser: User | null;
    allUsers: User[];
    users: User[];
    departments: Department[];
    setDepartments: React.Dispatch<React.SetStateAction<Department[]>>;
    channels: Channel[];
    directMessages: DirectMessage[];
    topics: Topic[];
    tickets: Ticket[];
    projects: Project[];
    messages: Message[];
    scheduledMessages: Message[];
    cancelScheduledMessage: (messageId: string) => Promise<void>;
    roles: Role[];
    settings: GlobalSettings | null;
    authToken: string | null;
    isOnline: boolean;

    // UI State
    activeView: ActiveView;
    setActiveView: (view: ActiveView) => void;
    activeThread: ThreadData | null;
    isLoading: boolean;
    isMessagesLoading: boolean;
    editingMessageId: string | null;
    setEditingMessageId: (id: string | null) => void;
    editContent: string;
    setEditContent: (content: string) => void;

    // Modal State
    showCreateChannel: boolean;
    setShowCreateChannel: (show: boolean) => void;
    showCreateTicket: boolean;
    setShowCreateTicket: (show: boolean) => void;
    showCreateProject: boolean;
    setShowCreateProject: (show: boolean) => void;
    showCreateTopic: boolean;
    setShowCreateTopic: (show: boolean) => void;
    showCreateDM: boolean;
    setShowCreateDM: (show: boolean) => void;
    showSettings: boolean;
    setShowSettings: (show: boolean) => void;

    // Actions
    sendMessage: (content: string, isEncrypted?: boolean, options?: { selfDestruct?: boolean, duration?: number, attachments?: any[], scheduledTime?: string | null }) => Promise<Message>;
    editMessage: (messageId: string, content: string, options?: { is_encrypted?: boolean, self_destruct?: boolean, attachments?: any[] }) => Promise<Message>;
    deleteMessage: (messageId: string) => Promise<void>;
    markMessageAsRead: (messageId: string) => Promise<Message>;
    toggleReaction: (messageId: string, emoji: string) => Promise<Message>;
    replyToThread: (content: string, isEncrypted?: boolean, options?: { attachments?: any[], broadcast?: boolean }) => Promise<Message | undefined>;
    createChannel: (name: string, allowMemberInvites?: boolean, member_ids?: string[], encryption_mode?: string) => Promise<Channel>;
    updateChannel: (channelId: string, data: Partial<Channel>) => Promise<Channel>;
    createTicket: (data: CreateTicketData) => Promise<Ticket>;
    updateTicket: (ticketId: string, data: UpdateTicketData) => Promise<Ticket>;
    deleteTicket: (ticketId: string) => Promise<void>;
    createProject: (data: CreateProjectData) => Promise<Project>;
    updateProject: (projectId: string, data: Partial<CreateProjectData> & { is_archived?: boolean }) => Promise<Project>;
    archiveProject: (projectId: string) => Promise<void>;
    refreshProjects: () => Promise<void>;
    moveProjectTicket: (projectId: string, ticketId: string, data: { section_id?: string | null; position?: number }) => Promise<Ticket>;
    bulkUpdateProjectTickets: (projectId: string, ticketIds: string[], changes: ProjectBulkChanges) => Promise<Ticket[]>;
    setTicketCustomField: (ticketId: string, fieldId: string, value: unknown) => Promise<Ticket>;
    createTopic: (data: { name: string; description?: string; color?: string; member_ids?: string[]; expires_at?: string; allow_member_invites?: boolean; encryption_mode?: string }) => Promise<Topic>;
    updateTopic: (topicId: string, data: Partial<Topic>) => Promise<Topic>;
    archiveTopic: (topicId: string) => Promise<void>;
    unarchiveTopic: (topicId: string) => Promise<void>;
    deleteTopic: (topicId: string) => Promise<void>;
    archiveChannel: (channelId: string) => Promise<void>;
    unarchiveChannel: (channelId: string) => Promise<void>;
    deleteChannel: (channelId: string) => Promise<void>;
    createDM: (participantIds: string[]) => Promise<DirectMessage>;
    updateUser: (userId: string, data: UpdateUserData) => Promise<User>;
    createDepartment: (data: CreateDepartmentData) => Promise<Department>;
    updateDepartment: (departmentId: string, data: Partial<Department>) => Promise<Department>;
    openThread: (messageId: string) => Promise<void>;
    closeThread: () => void;
    loadMessages: () => Promise<void>;

    // Admin Actions
    createRole: (data: Partial<Role>) => Promise<Role>;
    updateRole: (roleId: string, data: Partial<Role>) => Promise<Role>;
    deleteRole: (roleId: string) => Promise<void>;
    updateSettings: (data: Partial<GlobalSettings>) => Promise<GlobalSettings>;
    updateStatus: (type: User['status_type'], text?: string) => Promise<void>;
    uploadFile: (file: File) => Promise<FileRecord>;
    updateFileRetention: (fileId: string, data: { never_delete?: boolean, retention_days?: number }) => Promise<FileRecord>;
    requestNotificationPermission: () => Promise<boolean>;
    sendNotification: (title: string, options?: NotificationOptions) => void;
    login: (email: string, password: string) => Promise<any>;
    verify2FA: (userId: string, code: string, password?: string) => Promise<void>;
    logout: () => void;

    // E2EE
    recoverKeys: (password: string) => Promise<boolean>;
    myKeys: import('../utils/crypto').KeyPair | null;

    // Helpers
    getActiveViewData: () => Channel | DirectMessage | Topic | Ticket | { name: string; type: string } | null;
    getUserById: (id: string) => User | undefined;
    getDepartmentById: (id: string) => Department | undefined;
    hasPermission: (permission: string) => boolean;
    hasModuleAccess: (moduleId: ModuleId) => boolean;
    getAuthHeader: () => { Authorization: string };
    isAdmin: boolean;
    markAsUnread: (messageId: string) => Promise<Message>;
    snoozeMessage: (messageId: string, until: string) => Promise<Message>;
    unreadCounts: { channel: Record<string, number>; dm: Record<string, number>; ticket: Record<string, number>; topic: Record<string, number> };
    loadUnreadCounts: () => Promise<void>;

    // Socket real-time setters
    _setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    _setChannels: React.Dispatch<React.SetStateAction<Channel[]>>;
    _setDirectMessages: React.Dispatch<React.SetStateAction<DirectMessage[]>>;
    _setUsers: React.Dispatch<React.SetStateAction<User[]>>;
    _setTopics: React.Dispatch<React.SetStateAction<Topic[]>>;
    _setTickets: React.Dispatch<React.SetStateAction<Ticket[]>>;
    _setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
    _decryptMessages: (msgs: Message[]) => Message[];
    _setActiveThread: React.Dispatch<React.SetStateAction<ThreadData | null>>;
}

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

const AppContext = createContext<AppContextType | undefined>(undefined);

export function useApp(): AppContextType {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within AppProvider');
    }
    return context;
}

interface AppProviderProps {
    children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
    // Core state
    const [myKeys, setMyKeys] = useState<KeyPair | null>(null);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [allUsers, setAllUsers] = useState<User[]>([]);

    // Auto-filter soft-deleted users for standard UI logic. 
    // Admin uses allUsers to access deleted ghosts.
    const users = useMemo<User[]>(() => allUsers.filter(u => u.status !== 'deleted'), [allUsers]);

    const [departments, setDepartments] = useState<Department[]>([]);
    const [channels, setChannels] = useState<Channel[]>([]);
    const [directMessages, setDirectMessages] = useState<DirectMessage[]>([]);
    const [topics, setTopics] = useState<Topic[]>([]);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [scheduledMessages, setScheduledMessages] = useState<Message[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [settings, setSettings] = useState<GlobalSettings | null>(null);
    const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('auth_token'));
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    // Session Clobbering Protection
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'auth_token' && e.newValue !== authToken) {
                console.warn('[Session] Auth token changed in another tab. Reloading to prevent state corruption.');
                window.location.reload();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [authToken]);

    // Electron Sync Initialization
    useEffect(() => {
        const electronAPI = (window as any).electronAPI;
        if (electronAPI) {
            // Tell Electron where the API is
            const apiBaseURL = (import.meta as any).env.VITE_API_BASE_URL || window.location.origin;
            electronAPI.setApiUrl?.(apiBaseURL);

            // If we already have a token, start sync
            if (authToken) {
                electronAPI.setAuthToken?.(authToken);
            }

            // Listen for sync completion to refresh unread counts/messages
            const unsub = electronAPI.onSyncComplete?.((data: any) => {
                console.info(`[Sync] Desktop sync complete: ${data.count} messages updated.`);
                // Refresh local counts
                loadUnreadCounts();
            });

            return unsub;
        }
    }, [authToken]);

    // UI state
    const getInitialActiveView = (): ActiveView => {
        if (typeof window !== 'undefined' && window.history.state?.activeView) {
            return window.history.state.activeView;
        }
        return { type: 'channel', id: '1' };
    };

    const [activeView, _setActiveView] = useState<ActiveView>(getInitialActiveView);
    const activeViewRefForHistory = useRef<ActiveView>(activeView);

    useEffect(() => {
        activeViewRefForHistory.current = activeView;
    }, [activeView]);

    const setActiveView = useCallback((view: ActiveView, replace = false) => {
        if (view.type === activeViewRefForHistory.current.type && view.id === activeViewRefForHistory.current.id && !replace) {
            return;
        }

        _setActiveView(view);
        setIsMessagesLoading(true);
        setMessages([]); // Clear messages to prevent stale data/scrolling issues

        // Auto-close thread panel when navigating to non-chat views
        const nonChatViews = ['admin', 'ticket-calendar', 'ticket-dashboard', 'ticket-board', 'project-list', 'dept-queue', 'tickets-list'];
        if (nonChatViews.includes(view.type)) {
            setActiveThread(null);
        }

        if (replace) {
            window.history.replaceState({ ...window.history.state, activeView: view }, '', window.location.pathname + window.location.search);
        } else {
            window.history.pushState({ ...window.history.state, activeView: view }, '', window.location.pathname + window.location.search);
        }
    }, []);

    useEffect(() => {
        if (!window.history.state?.activeView) {
            window.history.replaceState({ ...window.history.state, activeView: getInitialActiveView() }, '', window.location.pathname + window.location.search);
        }

        const handlePopState = (event: PopStateEvent) => {
            if (event.state && event.state.activeView) {
                _setActiveView(event.state.activeView);
                setIsMessagesLoading(true);
                setMessages([]);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const [activeThread, setActiveThread] = useState<ThreadData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isMessagesLoading, setIsMessagesLoading] = useState(false);
    const messageLoadRequestRef = useRef(0);
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editContent, setEditContent] = useState('');

    // Modal state
    const [showCreateChannel, setShowCreateChannel] = useState(false);
    const [showCreateTicket, setShowCreateTicket] = useState(false);
    const [showCreateProject, setShowCreateProject] = useState(false);
    const [showCreateTopic, setShowCreateTopic] = useState(false);
    const [showCreateDM, setShowCreateDM] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    // Unread counts state
    const [unreadCounts, setUnreadCounts] = useState<{ channel: Record<string, number>; dm: Record<string, number>; ticket: Record<string, number>; topic: Record<string, number> }>({ channel: {}, dm: {}, ticket: {}, topic: {} });

    // Window focus tracking (for read receipts)
    const [isWindowFocused, setIsWindowFocused] = useState(document.hasFocus());
    useEffect(() => {
        const onFocus = () => setIsWindowFocused(true);
        const onBlur = () => setIsWindowFocused(false);
        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    // Helper to generate keys and save to server (used during registration)
    const generateAndSaveKeys = async (password: string): Promise<{ publicKey: string, encPrivateKey: string, salt: string, privateKey: string }> => {
        // 1. Generate Libsodium pair
        const keyPair = nacl.box.keyPair();
        const publicKey = encodeBase64(keyPair.publicKey);
        const privateKey = encodeBase64(keyPair.secretKey);

        // 2. Generate Salt
        const salt = generateSalt();

        // 3. Derive Wrapping Key
        const wrappingKey = await deriveKeyFromPassword(password, salt);

        // 4. Encrypt Private Key
        const encPrivateKey = await encryptPrivateKey(privateKey, wrappingKey);

        // 5. Store in LocalStorage (user-specific for session)
        const keys = { publicKey, privateKey };
        const storageKey = currentUser?.id ? `${STORAGE_KEY}_${currentUser.id}` : STORAGE_KEY;
        localStorage.setItem(storageKey, JSON.stringify(keys));
        setMyKeys(keys);

        return { publicKey, encPrivateKey, salt, privateKey };
    };

    const recoverKeys = async (password: string): Promise<boolean> => {
        try {
            if (!currentUser || !currentUser.enc_private_key || !currentUser.key_salt) {
                console.error('User or encrypted private key/salt not found for recovery.');
                return false;
            }

            const wrappingKey = await deriveKeyFromPassword(password, currentUser.key_salt);
            const decryptedPrivateKey = await decryptPrivateKey(currentUser.enc_private_key, wrappingKey);

            if (decryptedPrivateKey) {
                const keys: KeyPair = {
                    publicKey: currentUser.public_key!,
                    privateKey: decryptedPrivateKey
                };
                const storageKey = `${STORAGE_KEY}_${currentUser.id}`;
                localStorage.setItem(storageKey, JSON.stringify(keys));
                setMyKeys(keys);
                return true;
            } else {
                console.error('Failed to decrypt private key with provided password.');
                return false;
            }
        } catch (error) {
            console.error('Error during key recovery:', error);
            return false;
        }
    };


    const loadAllData = useCallback(async () => {
        setIsLoading(true);
        // Check for existing token
        const token = localStorage.getItem('auth_token');
        if (!token) {
            setIsLoading(false);
            return;
        }

        try {
            // 1. Ensure we have personal data and keys synced first (not parallel)
            // This prevents race conditions where messages or user lists are fetched
            // before the server has our current public key.
            const userData = await api.getCurrentUser();

            // --- Key Validation & Repair ---
            // On localhost/shared domains, users can "inherit" keys from each other via legacy slots.
            // We verify that whatever keys we have locally actually belong to this user's profile.
            const localKeys = getMyKeys(userData.id);
            console.log('[E2EE] Initial Data Load:', {
                userId: userData.id,
                serverPubKey: userData.public_key?.slice(0, 20),
                hasServerPrivateKey: !!userData.enc_private_key,
                localPubKey: localKeys?.publicKey?.slice(0, 20)
            });

            if (localKeys && userData.public_key && localKeys.publicKey !== userData.public_key) {
                console.error('[E2EE] KEY IDENTITY THEFT DETECTED: Local keys do not match server profile.', {
                    local: localKeys.publicKey.slice(0, 20),
                    server: userData.public_key.slice(0, 20)
                });
                localStorage.removeItem(`${STORAGE_KEY}_${userData.id}`);
                localStorage.removeItem(STORAGE_KEY); // Wipe legacy too
                setMyKeys(null);
            } else if (localKeys) {
                setMyKeys(localKeys);
            }

            // 3. Load remaining data in parallel
            const [
                usersData,
                deptData,
                channelsData,
                dmsData,
                topicsData,
                ticketsData,
                projectsData,
                rolesData,
                settingsData,
                unreadCountsData
            ] = await Promise.all([
                api.getUsers(),
                api.getDepartments(),
                api.getChannels(),
                api.getDMs(),
                api.getTopics(),
                api.getTickets(),
                api.getProjects(),
                api.getRoles(),
                api.getSettings(),
                api.getUnreadCounts()
            ]);

            setCurrentUser(userData);
            setAllUsers(usersData);
            setDepartments(deptData);
            setChannels(channelsData);
            setDirectMessages(dmsData);
            setTopics(topicsData);
            setTickets(ticketsData);
            setProjects(projectsData);
            setRoles(rolesData);
            setSettings(settingsData);

            // Load unread counts
            setUnreadCounts({
                channel: unreadCountsData.channel || {},
                dm: unreadCountsData.dm || {},
                ticket: unreadCountsData.ticket || {},
                topic: unreadCountsData.topic || {}
            });

            setIsLoading(false);
        } catch (error: any) {
            console.error('Failed to load initial data:', error);
            // If unauthorized, clear everything
            if (error.status === 401 || error.message?.includes('Unauthorized')) {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_user');
                setCurrentUser(null);
                setAuthToken(null);
            }
            setIsLoading(false);
        }
    }, []);

    // Also update authToken state if localStorage changes
    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (token !== authToken) {
            setAuthToken(token);
        }
    }, [authToken]);

    // Network connectivity listeners
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Initial data load
    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // Helper to decrypt a list of messages
    const decryptMessages = useCallback((msgs: Message[]): Message[] => {
        if (!Array.isArray(msgs)) return [];
        const myKeys = getMyKeys(currentUser?.id);
        const myPubKey = myKeys?.publicKey;

        return msgs.map(msg => {
            if ((msg as any).decrypted) return msg;

            if (msg.is_encrypted || (msg as any).is_encrypted === 1) {
                // Defensive check: if the content is already decrypted plaintext (i.e. not pure base64 ciphertext)
                if (msg.content && !msg.content.includes('[🔒') && !msg.content.startsWith('🔒 Encrypted') && msg.content.length > 0 && !/^[A-Za-z0-9+/=]+$/.test(msg.content)) {
                    return { ...msg, decrypted: true };
                }

                if (!msg.encrypted_keys) {
                    console.warn('[E2EE DECRYPT] is_encrypted=true but encrypted_keys missing', msg.id);
                    return msg;
                }

                if (!myPubKey) {
                    console.warn('[E2EE DECRYPT] No local keys for current user');
                    return { ...msg, content: '🔒 Encrypted Message (Unable to decrypt)' };
                }

                const encryptedKey = msg.encrypted_keys[myPubKey];

                // [DIAG] Prioritize author_public_key persisted ON THE MESSAGE.
                // This ensures decryption works even if the author's current key has changed (e.g. they were reinvited).
                let authorPubKey = (msg as any).author_public_key;

                // Fallback to current author profile if snapshot is missing
                if (!authorPubKey) authorPubKey = msg.author?.public_key;

                // Final fallback: try local users list
                if (!authorPubKey && msg.author_id) {
                    const localAuthor = users.find(u => u.id === msg.author_id);
                    if (localAuthor?.public_key) authorPubKey = localAuthor.public_key;
                }

                if (encryptedKey && authorPubKey) {
                    try {
                        console.log(`  Author public key used for decryption: ${authorPubKey.slice(0, 30)}...`);
                        const decryptedContent = decryptMessage(msg.content, encryptedKey, authorPubKey, currentUser?.id);
                        if (decryptedContent !== null) return { ...msg, content: decryptedContent, decrypted: true };
                    } catch (e) {
                        console.error('[E2EE DECRYPT] Error:', e);
                    }
                    console.warn('[E2EE DECRYPT] Keys matched but decryption failed — private key mismatch?');
                } else {
                    console.warn('[E2EE DECRYPT] Skipped:', { hasSharedKey: !!encryptedKey, hasAuthorKey: !!authorPubKey });
                }

                return { ...msg, content: '[🔒 Unable to decrypt]' };
            }
            return msg;
        });
    }, [users, currentUser?.id]);

    // Load messages when active view changes
    const loadMessages = useCallback(async () => {
        if (!activeView || !currentUser) return;

        const requestId = ++messageLoadRequestRef.current;
        const requestedViewKey = `${activeView.type}:${activeView.id}`;
        setIsMessagesLoading(true);

        try {
            let msgs: Message[] = [];

            // Check desktop cache first
            if ((window as any).electronAPI) {
                const filter: any = {};
                if (activeView.type === 'channel') filter.channel_id = activeView.id;
                else if (activeView.type === 'dm') filter.dm_id = activeView.id;

                // Only use local DB for Channels/DMs for now
                if (filter.channel_id || filter.dm_id) {
                    // TODO: In full implementation, we'd load local THEN sync delta
                    // For now, we just load local if available, or fall back to API if empty?
                    // Let's load from API for validity, then save to DB?
                    // Or just load from DB?
                    // User asked for "download to local machine".

                    // HYBRID STRATEGY:
                    // 1. Fetch from API (to ensure up to date)
                    // 2. Save to DB
                    // 3. Render
                }
            }

            if (activeView.type === 'channel') {
                msgs = await api.getChannelMessages(activeView.id);
            } else if (activeView.type === 'dm') {
                msgs = await api.getDMMessages(activeView.id);
            } else if (activeView.type === 'ticket') {
                msgs = await api.getTicketMessages(activeView.id);
            } else if (activeView.type === 'topic') {
                msgs = await api.getTopicMessages(activeView.id);
            } else if (activeView.type === 'inbox') {
                msgs = await api.getInbox();
            }

            // Desktop catch-up: Save loaded messages to local DB
            if ((window as any).electronAPI) {
                msgs.forEach(m => (window as any).electronAPI.saveMessage(m));
            }

            const currentView = activeViewRefForHistory.current;
            const currentViewKey = `${currentView.type}:${currentView.id}`;
            if (requestId === messageLoadRequestRef.current && requestedViewKey === currentViewKey) {
                setMessages(decryptMessages(msgs));
            }
        } catch (error) {
            console.error('Failed to load messages:', error);

            // Offline Fallback: Load from local DB
            if ((window as any).electronAPI) {
                const filter: any = {};
                if (activeView.type === 'channel') filter.channel_id = activeView.id;
                else if (activeView.type === 'dm') filter.dm_id = activeView.id;

                if (filter.channel_id || filter.dm_id) {
                    const localMsgs = await (window as any).electronAPI.getMessages(filter, 100, 0);
                    if (localMsgs && localMsgs.length > 0) {
                        const currentView = activeViewRefForHistory.current;
                        const currentViewKey = `${currentView.type}:${currentView.id}`;
                        if (requestId === messageLoadRequestRef.current && requestedViewKey === currentViewKey) {
                            setMessages(decryptMessages(localMsgs));
                        }
                        return;
                    }
                }
            }
        } finally {
            if (requestId === messageLoadRequestRef.current) {
                setIsMessagesLoading(false);
            }
        }
    }, [activeView, decryptMessages, currentUser]);

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    const loadUnreadCounts = async () => {
        try {
            const counts = await api.getUnreadCounts();
            setUnreadCounts({
                channel: counts.channel || {},
                dm: counts.dm || {},
                ticket: counts.ticket || {},
                topic: counts.topic || {}
            });
        } catch (error) {
            console.error('Failed to load unread counts:', error);
        }
    };

    // Helper to restore E2EE keys
    const restoreKeys = async (userData: any, password?: string) => {
        console.log('[E2EE] restoreKeys triggered. User E2EE state:', {
            hasPubKey: !!userData.public_key,
            hasEncPrivateKey: !!userData.enc_private_key,
            hasSalt: !!userData.key_salt
        });
        if (!userData || !password) return;
        if (userData.enc_private_key && userData.key_salt) {
            try {
                const wrappingKey = await deriveKeyFromPassword(password, userData.key_salt);
                const decryptedPrivateKey = await decryptPrivateKey(userData.enc_private_key, wrappingKey);
                if (decryptedPrivateKey && userData.public_key) {
                    // ALWAYS restore from the server blob — this is the canonical source of truth.
                    const keys: KeyPair = { publicKey: userData.public_key, privateKey: decryptedPrivateKey };
                    const storageKey = `${STORAGE_KEY}_${userData.id}`;
                    localStorage.setItem(storageKey, JSON.stringify(keys));
                    setMyKeys(keys);
                    console.info('[E2EE] Keys restored from server blob. pubkey (first 20):', keys.publicKey.slice(0, 20));
                } else {
                    console.warn('[E2EE] Key decryption failed — wrong password or corrupted blob.');
                }
            } catch (e) {
                console.error('[E2EE] Error restoring keys:', e);
            }
        } else if (!userData.enc_private_key) {
            // No keys on server yet — generate a fresh pair
            try {
                const { publicKey, encPrivateKey, salt, privateKey } = await generateAndSaveKeys(password);
                const { recoveryKey, encPrivateKeyEscrow } = await generateEscrowKey(privateKey);
                await api.saveKeys({
                    enc_private_key: encPrivateKey,
                    key_salt: salt,
                    public_key: publicKey,
                    enc_private_key_escrow: encPrivateKeyEscrow,
                    recovery_key: recoveryKey,
                });
                // Also persist immediately to localStorage + React state
                const freshKeys: KeyPair = { publicKey, privateKey };
                const storageKey = `${STORAGE_KEY}_${userData.id}`;
                localStorage.setItem(storageKey, JSON.stringify(freshKeys));
                setMyKeys(freshKeys);
                console.info('[E2EE] New keypair generated and uploaded to server. pubkey (first 20):', publicKey.slice(0, 20));
            } catch (e) {
                console.error('[E2EE] Error generating/uploading keys:', e);
            }
        }
    };

    // Actions
    const login = async (email: string, password: string): Promise<any> => {
        const response = await api.login(email, password);
        if (response.success && !response.requires_2fa) {
            setAuthToken(response.token || null);

            // --- E2EE Key Recovery / Initialization ---
            await restoreKeys(response.user, password);

            // Now that keys are in localStorage, load data (which fetches messages)
            await loadAllData();

            // Desktop: Initialize encrypted local database with derived key
            if ((window as any).electronAPI) {
                await (window as any).electronAPI.initDatabase(password);
            }
        }
        return response;
    };

    const verify2FA = async (userId: string, code: string, password?: string): Promise<void> => {
        const response = await api.verify2FA(userId, code);
        if (response.success && response.token) {
            localStorage.setItem('auth_token', response.token);
            setAuthToken(response.token);

            // Restore keys if password is provided
            if (password && response.user) {
                await restoreKeys(response.user, password);
            }

            await loadAllData();
        }
    };

    const sendMessage = async (content: string, isEncrypted = false, options?: { selfDestruct?: boolean, duration?: number, attachments?: any[], scheduledTime?: string | null }): Promise<Message> => {
        let duration = options?.duration;
        // Enforce global settings if custom timer is disabled
        if (settings && !settings.allow_user_custom_timer) {
            duration = settings.default_self_destruct_timer;
        }

        const messageData: any = {
            content,
            self_destruct: options?.selfDestruct,
            duration: duration,
            attachments: options?.attachments || [],
            scheduled_at: options?.scheduledTime || undefined
        };

        if (activeView.type === 'channel') {
            messageData.channel_id = activeView.id;
        } else if (activeView.type === 'dm') {
            messageData.dm_id = activeView.id;
        } else if (activeView.type === 'ticket') {
            messageData.ticket_id = activeView.id;
        } else if (activeView.type === 'topic') {
            messageData.topic_id = activeView.id;
        }

        // Handle Encryption
        if (isEncrypted && (messageData.channel_id || messageData.dm_id || messageData.topic_id)) {
            let members: string[] = [];

            if (messageData.channel_id) {
                const channel = channels.find(c => c.id === messageData.channel_id);
                if (channel) members = channel.member_ids;
            } else if (messageData.dm_id) {
                const dm = directMessages.find(d => d.id === messageData.dm_id);
                if (dm) members = dm.participant_ids;
            } else if (messageData.topic_id) {
                const topic = topics.find(t => t.id === messageData.topic_id);
                if (topic) members = topic.member_ids;
            }

            // CRITICAL: Fetch fresh user data from server right before encrypting.
            // This guarantees we have up-to-date public keys even if state is stale.
            let freshUsers: User[];
            try {
                freshUsers = await api.getUsers();
            } catch {
                freshUsers = users;
                console.warn('[E2EE] Failed to fetch fresh user keys — falling back to cached state');
            }

            const membersWithKeys = members.map(uid => ({
                id: uid,
                key: (uid === currentUser?.id)
                    ? (getMyKeys(currentUser?.id)?.publicKey || currentUser?.public_key)
                    : freshUsers.find(u => u.id === uid)?.public_key
            }));

            // Always ensure sender is in the list so they can decrypt their own messages
            if (currentUser && !members.includes(currentUser.id)) {
                membersWithKeys.push({
                    id: currentUser.id,
                    key: getMyKeys(currentUser?.id)?.publicKey || currentUser.public_key
                });
            }


            const missingKeys = membersWithKeys.filter(m => !m.key);

            if (missingKeys.length > 0) {
                const names = missingKeys
                    .map(m => freshUsers.find(u => u.id === m.id)?.name || m.id)
                    .join(', ');
                throw new Error(`Cannot send encrypted message: One or more recipients (${names}) haven't set up encryption keys yet.`);
            }

            const recipientKeys = membersWithKeys.map(m => m.key as string);

            if (recipientKeys.length > 0) {
                const { encryptedContent, encryptedKeys } = encryptMessage(content, recipientKeys, currentUser?.id);
                messageData.content = encryptedContent;
                messageData.is_encrypted = true;
                messageData.encrypted_keys = encryptedKeys;
            }
        }

        const newMessage = await api.sendMessage(messageData);
        // If we sent it encrypted, we know the content essentially, but for consistency let's just use the local content
        // In a real app we'd optimistic UI. Here we just swap content back to plaintext for local display
        const displayMessage = { ...newMessage };
        if (isEncrypted) {
            // Show plaintext content locally (we know what we sent)
            // But keep is_encrypted=true so the lock icon shows
            displayMessage.content = content;
            displayMessage.is_encrypted = true;
            (displayMessage as any).decrypted = true;
        }

        // Desktop: Persist to local DB
        if ((window as any).electronAPI) {
            (window as any).electronAPI.saveMessage(displayMessage);
        }

        // Scheduled messages go to their own list, not the main chat
        if (options?.scheduledTime) {
            setScheduledMessages(prev => {
                if (prev.some(m => m.id === displayMessage.id)) return prev;
                return [...prev, { ...displayMessage, content: isEncrypted ? content : displayMessage.content }];
            });
        } else {
            setMessages(prev => {
                if (prev.some(m => m.id === displayMessage.id)) return prev;
                return [...prev, displayMessage];
            });
        }
        return newMessage;
    };

    const cancelScheduledMessage = async (messageId: string): Promise<void> => {
        await api.deleteMessage(messageId);
        setScheduledMessages(prev => prev.filter(m => m.id !== messageId));
    };

    const editMessage = async (messageId: string, content: string, options: { is_encrypted?: boolean, self_destruct?: boolean, attachments?: any[] } = {}): Promise<Message> => {
        let finalContent = content;
        let encryptedKeys: any = undefined;

        const originalMessage = messages.find(m => m.id === messageId);
        if (options.is_encrypted && originalMessage) {
            let members: string[] = [];

            if (originalMessage.channel_id) {
                const channel = channels.find(c => c.id === originalMessage.channel_id);
                if (channel) members = channel.member_ids;
            } else if (originalMessage.dm_id) {
                const dm = directMessages.find(d => d.id === originalMessage.dm_id);
                if (dm) members = dm.participant_ids;
            } else if (originalMessage.topic_id) {
                const topic = topics.find(t => t.id === originalMessage.topic_id);
                if (topic) members = topic.member_ids;
            }

            // Fetch fresh user data from server right before encrypting
            let freshUsers: User[];
            try {
                freshUsers = await api.getUsers();
            } catch {
                freshUsers = users;
                console.warn('[E2EE] Failed to fetch fresh user keys — falling back to cached state');
            }

            const membersWithKeys = members.map(uid => ({
                id: uid,
                key: (uid === currentUser?.id)
                    ? (getMyKeys(currentUser?.id)?.publicKey || currentUser?.public_key)
                    : freshUsers.find(u => u.id === uid)?.public_key
            }));

            // Always ensure sender is in the list
            if (currentUser && !members.includes(currentUser.id)) {
                membersWithKeys.push({
                    id: currentUser.id,
                    key: getMyKeys(currentUser?.id)?.publicKey || currentUser.public_key
                });
            }

            const missingKeys = membersWithKeys.filter(m => !m.key);
            if (missingKeys.length > 0) {
                const names = missingKeys
                    .map(m => freshUsers.find(u => u.id === m.id)?.name || m.id)
                    .join(', ');
                throw new Error(`Cannot edit encrypted message: One or more recipients (${names}) haven't set up encryption keys yet.`);
            }

            const recipientKeys = membersWithKeys.map(m => m.key as string);
            if (recipientKeys.length > 0) {
                const { encryptedContent, encryptedKeys: newEncryptedKeys } = encryptMessage(content, recipientKeys, currentUser?.id);
                finalContent = encryptedContent;
                encryptedKeys = newEncryptedKeys;
            }
        }

        const updateData: any = { content: finalContent, ...options };
        if (encryptedKeys) {
            updateData.encrypted_keys = encryptedKeys;
            // Provide current public key so recipients know which one to verify against
            updateData.author_public_key = getMyKeys(currentUser?.id)?.publicKey || currentUser?.public_key;
        }

        const updated = await api.editMessage(messageId, updateData);

        const displayMessage = { ...originalMessage, ...updated };
        if (options.is_encrypted) {
            // Show plaintext locally while preserving is_encrypted=true flag
            displayMessage.content = content;
            displayMessage.is_encrypted = true;
            (displayMessage as any).decrypted = true;
        }

        setMessages(prev => prev.map(m => m.id === messageId ? displayMessage : m));

        // Desktop: update local DB
        if ((window as any).electronAPI) {
            (window as any).electronAPI.saveMessage(displayMessage);
        }

        return displayMessage;
    };

    const deleteMessage = async (messageId: string): Promise<void> => {
        const updated = await api.deleteMessage(messageId);

        const originalMessage = messages.find(m => m.id === messageId);
        if (originalMessage) {
            const mergedMessage = { ...originalMessage, ...updated, content: originalMessage.content };
            setMessages(prev => prev.map(m => m.id === messageId ? mergedMessage : m));
            if ((window as any).electronAPI) {
                (window as any).electronAPI.saveMessage(mergedMessage);
            }
        }
    };

    const markMessageAsRead = async (messageId: string): Promise<Message> => {
        const updated = await api.markMessageAsRead(messageId);

        const originalMessage = messages.find(m => m.id === messageId);
        let mergedMessage: any = null;

        if (originalMessage) {
            mergedMessage = { ...originalMessage, ...updated, content: originalMessage.content };
            setMessages(prev => prev.map(m => m.id === messageId ? mergedMessage : m));
        } else {
            setMessages(prev => prev.map(m => m.id === messageId ? { ...m, ...updated } : m));
        }

        setActiveThread(prev => {
            if (prev && prev.replies) {
                const originalReply = prev.replies.find(r => r.id === messageId);
                if (originalReply) {
                    mergedMessage = { ...originalReply, ...updated, content: originalReply.content };
                    return {
                        ...prev,
                        replies: prev.replies.map(r => r.id === messageId ? mergedMessage : r)
                    };
                }
            }
            return prev;
        });

        if (mergedMessage && (window as any).electronAPI) {
            (window as any).electronAPI.saveMessage(mergedMessage);
        }

        loadUnreadCounts();
        return updated;
    };

    const snoozeMessage = async (messageId: string, until: string): Promise<Message> => {
        const updated = await api.snoozeMessage(messageId, until);

        const originalMessage = messages.find(m => m.id === messageId);
        if (originalMessage) {
            const mergedMessage = { ...originalMessage, ...updated, content: originalMessage.content };
            setMessages(prev => prev.map(m => m.id === messageId ? mergedMessage : m));
            if ((window as any).electronAPI) {
                (window as any).electronAPI.saveMessage(mergedMessage);
            }
        } else {
            setMessages(prev => prev.map(m => m.id === messageId ? updated : m));
        }

        loadUnreadCounts();
        return updated;
    };

    const toggleReaction = async (messageId: string, emoji: string): Promise<Message> => {
        const updated = await api.toggleReaction(messageId, emoji);
        // Decrypt before merging to prevent overwriting decrypted content with ciphertext
        const [decryptedUpdate] = decryptMessages([updated]);

        const originalMessage = messages.find(m => m.id === messageId);
        const mergedMessage = originalMessage
            ? { ...originalMessage, reactions: decryptedUpdate.reactions }
            : { ...updated, reactions: decryptedUpdate.reactions };

        setMessages(prev => prev.map(m => m.id === messageId ? mergedMessage : m));

        if ((window as any).electronAPI) {
            (window as any).electronAPI.saveMessage(mergedMessage);
        }

        return updated;
    };

    const replyToThread = async (content: string, isEncrypted = false, options?: { attachments?: any[], broadcast?: boolean }): Promise<Message | undefined> => {
        if (!activeThread) return;

        const messageData: any = {
            content,
            attachments: options?.attachments || [],
            is_broadcast: options?.broadcast
        };

        const parent = activeThread.parent;

        // Handle Encryption
        if (isEncrypted) {
            let members: string[] = [];

            if (parent.channel_id) {
                const channel = channels.find(c => c.id === parent.channel_id);
                if (channel) members = channel.member_ids;
            } else if (parent.dm_id) {
                const dm = directMessages.find(d => d.id === parent.dm_id);
                if (dm) members = dm.participant_ids;
            } else if (parent.topic_id) {
                const topic = topics.find(t => t.id === parent.topic_id);
                if (topic) members = topic.member_ids;
            }

            // Fetch fresh user data from server right before encrypting
            let freshUsers: User[];
            try {
                freshUsers = await api.getUsers();
            } catch {
                freshUsers = users;
                console.warn('[E2EE] Failed to fetch fresh user keys — falling back to cached state');
            }

            const membersWithKeys = members.map(uid => ({
                id: uid,
                key: (uid === currentUser?.id)
                    ? (getMyKeys(currentUser?.id)?.publicKey || currentUser?.public_key)
                    : freshUsers.find(u => u.id === uid)?.public_key
            }));

            // Always ensure sender is in the list
            if (currentUser && !members.includes(currentUser.id)) {
                membersWithKeys.push({
                    id: currentUser.id,
                    key: getMyKeys(currentUser?.id)?.publicKey || currentUser.public_key
                });
            }

            const missingKeys = membersWithKeys.filter(m => !m.key);
            if (missingKeys.length > 0) {
                const names = missingKeys
                    .map(m => freshUsers.find(u => u.id === m.id)?.name || m.id)
                    .join(', ');
                throw new Error(`Cannot send encrypted message: One or more recipients (${names}) haven't set up encryption keys yet.`);
            }

            const recipientKeys = membersWithKeys.map(m => m.key as string);
            if (recipientKeys.length > 0) {
                const { encryptedContent, encryptedKeys } = encryptMessage(content, recipientKeys, currentUser?.id);
                messageData.content = encryptedContent;
                messageData.is_encrypted = true;
                messageData.encrypted_keys = encryptedKeys;
            }
        }

        const reply = await api.replyToThread(parent.id, messageData);

        // If we sent it encrypted, we know the content essentially, but for consistency let's just use the local content
        // In a real app we'd optimistic UI. Here we just swap content back to plaintext for local display
        const displayReply = { ...reply };
        if (isEncrypted) {
            displayReply.content = content;
            displayReply.is_encrypted = true;
            (displayReply as any).decrypted = true;
        }

        // Decrypt before adding to thread state
        const [decryptedReply] = decryptMessages([displayReply]);

        if ((window as any).electronAPI) {
            (window as any).electronAPI.saveMessage(decryptedReply);
        }

        setActiveThread(prev => {
            if (!prev) return null;
            const exists = prev.replies?.some(r => r.id === decryptedReply.id);
            if (exists) {
                return {
                    ...prev,
                    replies: prev.replies.map(r => r.id === decryptedReply.id ? decryptedReply : r)
                };
            }
            return {
                ...prev,
                replies: [...(prev.replies || []), decryptedReply]
            };
        });

        return decryptedReply;
    };

    const createChannel = async (name: string, allowMemberInvites = true, member_ids?: string[], encryption_mode?: string): Promise<Channel> => {
        const channel = await api.createChannel({
            name,
            is_private: true,
            allow_member_invites: allowMemberInvites,
            member_ids,
            encryption_mode: encryption_mode as any
        });
        setChannels(prev => {
            if (prev.some(c => c.id === channel.id)) return prev;
            return [...prev, channel];
        });
        setShowCreateChannel(false);
        setActiveView({ type: 'channel', id: channel.id });
        return channel;
    };

    const updateChannel = async (channelId: string, data: Partial<Channel>): Promise<Channel> => {
        const updated = await api.updateChannel(channelId, data);
        setChannels(prev => prev.map(c => c.id === channelId ? updated : c));
        return updated;
    };

    const archiveChannel = async (channelId: string): Promise<void> => {
        await api.archiveChannel(channelId);
        setChannels(prev => prev.filter(c => c.id !== channelId));
        if (activeView.type === 'channel' && activeView.id === channelId) {
            setActiveView({ type: 'inbox', id: 'inbox' });
        }
    };

    const unarchiveChannel = async (channelId: string): Promise<void> => {
        const channel = await api.unarchiveChannel(channelId);
        setChannels(prev => [...prev, channel]);
    };

    const deleteChannel = async (channelId: string): Promise<void> => {
        await api.deleteChannel(channelId);
        setChannels(prev => prev.filter(c => c.id !== channelId));
        if (activeView.type === 'channel' && activeView.id === channelId) {
            setActiveView({ type: 'inbox', id: 'inbox' });
        }
    };

    const createTicket = async (data: CreateTicketData): Promise<Ticket> => {
        const ticket = await api.createTicket(data);
        setTickets(prev => {
            if (prev.some(t => t.id === ticket.id)) return prev;
            return [...prev, ticket];
        });
        setProjects(prev => prev.map(project => {
            if (!ticket.projects?.some(link => link.project_id === project.id)) return project;
            const ticketIds = [...new Set([...project.ticket_ids, ticket.id])];
            return { ...project, ticket_ids: ticketIds, ticket_count: ticketIds.length };
        }));
        setShowCreateTicket(false);
        setActiveView({ type: 'ticket', id: ticket.id });
        return ticket;
    };

    const updateTicket = async (ticketId: string, data: UpdateTicketData): Promise<Ticket> => {
        const updated = await api.updateTicket(ticketId, data);
        setTickets(prev => prev.map(t => t.id === ticketId ? updated : t));
        return updated;
    };

    const deleteTicket = async (ticketId: string): Promise<void> => {
        await api.deleteTicket(ticketId);
        setTickets(prev => prev.filter(t => t.id !== ticketId));
        setProjects(prev => prev.map(project => {
            const ticketIds = project.ticket_ids.filter(id => id !== ticketId);
            return { ...project, ticket_ids: ticketIds, ticket_count: ticketIds.length };
        }));
        if (activeView.type === 'ticket' && activeView.id === ticketId) {
            setActiveView({ type: 'tickets-list', id: 'all' });
        }
    };

    const createProject = async (data: CreateProjectData): Promise<Project> => {
        const project = await api.createProject(data);
        setProjects(prev => [...prev.filter(item => item.id !== project.id), project]
            .sort((left, right) => left.name.localeCompare(right.name)));
        return project;
    };

    const updateProject = async (projectId: string, data: Partial<CreateProjectData> & { is_archived?: boolean }): Promise<Project> => {
        const project = await api.updateProject(projectId, data);
        setProjects(prev => prev.map(item => item.id === projectId ? project : item));
        return project;
    };

    const archiveProject = async (projectId: string): Promise<void> => {
        await api.archiveProject(projectId);
        setProjects(prev => prev.filter(item => item.id !== projectId));
        if (activeView.id === projectId) setActiveView({ type: 'ticket-board', id: 'all' });
    };

    const refreshProjects = async (): Promise<void> => {
        const freshProjects = await api.getProjects();
        setProjects(freshProjects);
    };

    const moveProjectTicket = async (projectId: string, ticketId: string, data: { section_id?: string | null; position?: number }): Promise<Ticket> => {
        const ticket = await api.moveProjectTicket(projectId, ticketId, data);
        setTickets(prev => prev.map(item => item.id === ticket.id ? ticket : item));
        return ticket;
    };

    const bulkUpdateProjectTickets = async (projectId: string, ticketIds: string[], changes: ProjectBulkChanges): Promise<Ticket[]> => {
        const result = await api.bulkUpdateProjectTickets(projectId, ticketIds, changes);
        const updates = new Map(result.tickets.map(ticket => [ticket.id, ticket]));
        setTickets(prev => prev.map(ticket => updates.get(ticket.id) || ticket));
        return result.tickets;
    };

    const setTicketCustomField = async (ticketId: string, fieldId: string, value: unknown): Promise<Ticket> => {
        const ticket = await api.setTicketCustomField(ticketId, fieldId, value);
        setTickets(prev => prev.map(item => item.id === ticket.id ? ticket : item));
        return ticket;
    };

    const createTopic = async (data: { name: string; description?: string; color?: string; member_ids?: string[]; expires_at?: string; allow_member_invites?: boolean; encryption_mode?: string }): Promise<Topic> => {
        const topic = await api.createTopic(data);
        setTopics(prev => {
            if (prev.some(t => t.id === topic.id)) return prev;
            return [...prev, topic];
        });
        setActiveView({ type: 'topic', id: topic.id });
        return topic;
    };

    const updateTopic = async (topicId: string, data: Partial<Topic>): Promise<Topic> => {
        const updated = await api.updateTopic(topicId, data);
        setTopics(prev => prev.map(t => t.id === topicId ? updated : t));
        return updated;
    };

    const archiveTopic = async (topicId: string): Promise<void> => {
        await api.archiveTopic(topicId);
        setTopics(prev => prev.filter(t => t.id !== topicId)); // Remove from active list
        if (activeView.type === 'topic' && activeView.id === topicId) {
            setActiveView({ type: 'inbox', id: 'inbox' });
        }
    };

    const unarchiveTopic = async (topicId: string): Promise<void> => {
        const topic = await api.unarchiveTopic(topicId);
        setTopics(prev => [...prev, topic]); // Add back to active list
    };

    const deleteTopic = async (topicId: string): Promise<void> => {
        await api.deleteTopic(topicId);
        setTopics(prev => prev.filter(t => t.id !== topicId));
        if (activeView.type === 'topic' && activeView.id === topicId) {
            setActiveView({ type: 'inbox', id: 'inbox' });
        }
    };

    const createDM = async (participantIds: string[]): Promise<DirectMessage> => {
        const dm = await api.createDM(participantIds);
        setDirectMessages(prev => {
            if (prev.some(d => d.id === dm.id)) return prev;
            return [...prev, dm];
        });
        setShowCreateDM(false);
        setActiveView({ type: 'dm', id: dm.id });
        return dm;
    };

    const updateUser = async (userId: string, data: UpdateUserData): Promise<User> => {
        const updated = await api.updateUser(userId, data);
        setAllUsers(prev => prev.map(u => u.id === userId ? updated : u));
        if (currentUser && currentUser.id === userId) {
            setCurrentUser(updated);
        }
        return updated;
    };

    const createDepartment = async (data: CreateDepartmentData): Promise<Department> => {
        const newDept = await api.createDepartment(data);
        setDepartments(prev => {
            if (prev.some(d => d.id === newDept.id)) return prev;
            return [...prev, newDept];
        });
        return newDept;
    };

    const uploadFile = (file: File) => api.uploadFile(file);

    const openThread = async (messageId: string): Promise<void> => {
        const threadData = await api.getThreadReplies(messageId);
        if (threadData) {
            const [decryptedParent] = decryptMessages([threadData.parent]);
            const decryptedReplies = decryptMessages(threadData.replies || []);
            setActiveThread({
                parent: decryptedParent,
                replies: decryptedReplies
            });
        }
    };

    const closeThread = (): void => {
        setActiveThread(null);
    };

    const getActiveViewData = () => {
        if (!activeView) return null;

        switch (activeView.type) {
            case 'channel':
                return channels.find(c => c.id === activeView.id) || null;
            case 'dm':
                return directMessages.find(d => d.id === activeView.id) || null;
            case 'ticket':
                return tickets.find(t => t.id === activeView.id) || null;
            case 'topic':
                return topics.find(t => t.id === activeView.id) || null;
            case 'inbox':
                return { name: 'Inbox', type: 'inbox' };
            default:
                return null;
        }
    };

    const getUserById = (id: string): User | undefined => users.find(u => u.id === id);
    const getDepartmentById = (id: string): Department | undefined => departments.find(d => d.id === id);


    const createRole = async (data: Partial<Role>): Promise<Role> => {
        const role = await api.createRole(data);
        setRoles(prev => {
            if (prev.some(r => r.id === role.id)) return prev;
            return [...prev, role];
        });
        return role;
    };

    const updateRole = async (roleId: string, data: Partial<Role>): Promise<Role> => {
        const updated = await api.updateRole(roleId, data);
        setRoles(prev => prev.map(r => r.id === roleId ? updated : r));
        return updated;
    };

    const deleteRole = async (roleId: string): Promise<void> => {
        await api.deleteRole(roleId);
        setRoles(prev => prev.filter(r => r.id !== roleId));
    };

    const updateSettings = async (data: Partial<GlobalSettings>) => {
        const updated = await api.updateSettings(data);
        console.log('[AppContext] updateSettings response:', updated);
        setSettings(updated);
        return updated;
    };

    const updateFileRetention = (fileId: string, data: { never_delete?: boolean, retention_days?: number }) =>
        api.updateFileRetention(fileId, data);

    const updateDepartment = async (departmentId: string, data: Partial<Department>): Promise<Department> => {
        const updated = await api.updateDepartment(departmentId, data);
        setDepartments(prev => prev.map(d => d.id === departmentId ? updated : d));
        return updated;
    };

    const markAsUnread = async (messageId: string): Promise<Message> => {
        const updated = await api.markAsUnread(messageId);

        const originalMessage = messages.find(m => m.id === messageId);
        if (originalMessage) {
            const createdAt = new Date(originalMessage.created_at).getTime();
            setMessages(prev => prev.map(m => {
                if (new Date(m.created_at).getTime() >= createdAt) {
                    return {
                        ...m,
                        read_by: (m.read_by || []).filter((r: any) => r.user_id !== currentUser?.id)
                    };
                }
                return m;
            }));
        } else {
            setMessages(prev => prev.map(m => m.id === messageId ? updated : m));
        }

        // Suppress auto-read for this message temporarily so it isn't instantly re-read
        // Store the marked-unread message IDs in a ref so the auto-read effect skips them
        markedUnreadIds.current.add(messageId);
        setTimeout(() => markedUnreadIds.current.delete(messageId), 10000);

        loadUnreadCounts();
        return updated;
    };

    const updateStatus = async (type: User['status_type'], text?: string) => {
        if (!currentUser) return;
        // Optimistic update: reflect immediately for the current user
        setCurrentUser(prev => prev ? { ...prev, status_type: type, status_text: text ?? '' } : null);
        setAllUsers(prev => prev.map(u => u.id === currentUser.id
            ? { ...u, status_type: type, status_text: text ?? '' }
            : u
        ));
        await updateUser(currentUser.id, { status_type: type, status_text: text || '' });
    };

    // Idle Detection
    useEffect(() => {
        if (!currentUser) return;

        let lastActivity = Date.now();
        let idleTimer: NodeJS.Timeout;

        const resetIdle = () => {
            lastActivity = Date.now();
            if (currentUser.status_type === 'away' && currentUser.status_text === 'Auto-away') {
                updateStatus('online');
            }
        };

        const checkIdle = async () => {
            let isIdle = false;

            // If running in Desktop app, check system-wide OS idle time
            if ((window as any).electronAPI?.getSystemIdleTime) {
                try {
                    const idleSecs = await (window as any).electronAPI.getSystemIdleTime();
                    isIdle = (idleSecs * 1000) > IDLE_TIMEOUT_MS;
                } catch (e) {
                    console.error('Failed to get system idle time:', e);
                    isIdle = Date.now() - lastActivity > IDLE_TIMEOUT_MS;
                }
            } else {
                // Web app fallback: check app window activity
                isIdle = Date.now() - lastActivity > IDLE_TIMEOUT_MS;
            }

            if (isIdle) {
                if (currentUser.status_type === 'online') {
                    updateStatus('away', 'Auto-away');
                }
            } else {
                // If they became active again globally, reset them
                if (currentUser.status_type === 'away' && currentUser.status_text === 'Auto-away') {
                    updateStatus('online');
                }
            }
        };

        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(e => window.addEventListener(e, resetIdle));

        idleTimer = setInterval(checkIdle, 60000); // Check every minute

        return () => {
            events.forEach(e => window.removeEventListener(e, resetIdle));
            clearInterval(idleTimer);
        };
    }, [currentUser?.status_type, updateStatus]); // Added updateStatus to dependencies

    const logout = useCallback(async () => {
        try {
            await api.logout();
        } catch (e) {
            console.error('Logout failed:', e);
        }
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem(STORAGE_KEY);
        setCurrentUser(null);
        setAuthToken(null);
        setMyKeys(null);
        setMessages([]);
        setTickets([]);
        setProjects([]);
        setChannels([]);
        setDirectMessages([]);
    }, []);

    // Notifications
    const requestNotificationPermission = async () => {
        if (!('Notification' in window)) return false;
        const permission = await Notification.requestPermission();
        return permission === 'granted';
    };

    const sendNotification = (title: string, options?: NotificationOptions) => {
        // Check for Electron API first
        if ((window as any).electronAPI) {
            (window as any).electronAPI.showNotification(title, options?.body || '');
            return;
        }

        // Fallback to Web API
        if (!('Notification' in window)) return;
        if (Notification.permission === 'granted') {
            new Notification(title, options);
        }
    };

    // Taskbar Badge (Electron)
    useEffect(() => {
        if (!(window as any).electronAPI) return;

        const totalUnread =
            Object.values(unreadCounts.channel || {}).reduce((a, b) => a + b, 0) +
            Object.values(unreadCounts.dm || {}).reduce((a, b) => a + b, 0) +
            Object.values(unreadCounts.ticket || {}).reduce((a, b) => a + b, 0) +
            Object.values(unreadCounts.topic || {}).reduce((a, b) => a + b, 0);

        if (totalUnread <= 0) {
            (window as any).electronAPI.setBadge(null);
            return;
        }

        // Draw badge on canvas
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Circle
            ctx.fillStyle = '#ff4444';
            ctx.beginPath();
            ctx.arc(16, 16, 14, 0, Math.PI * 2);
            ctx.fill();

            // Border
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Text
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 18px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const displayCount = totalUnread > 99 ? '99+' : totalUnread.toString();
            ctx.fillText(displayCount, 16, 16);

            (window as any).electronAPI.setBadge(canvas.toDataURL());
        }
    }, [unreadCounts]);

    // Ref to track recently marked-unread message IDs to prevent instant re-read
    const markedUnreadIds = useRef<Set<string>>(new Set());

    // Auto-mark messages as read
    useEffect(() => {
        if (!settings?.enable_read_receipts || !currentUser || !isWindowFocused) return;

        const promises: Promise<any>[] = [];
        messages.forEach(msg => {
            // Check if message belongs to current view
            let matchesView = false;
            if (activeView.type === 'channel' && msg.channel_id === activeView.id) matchesView = true;
            if (activeView.type === 'dm' && msg.dm_id === activeView.id) matchesView = true;
            if (activeView.type === 'ticket' && msg.ticket_id === activeView.id) matchesView = true;
            if (activeView.type === 'topic' && msg.topic_id === activeView.id) matchesView = true;
            // Do not auto-read messages in inbox view — user must open the specific chat

            if (matchesView && msg.author_id !== currentUser.id) {
                // Skip messages the user just manually marked as unread
                if (markedUnreadIds.current.has(msg.id)) return;
                // Check if already read by current user
                const isRead = msg.read_by?.some(r => r.user_id === currentUser.id);
                if (!isRead) {
                    promises.push(api.markMessageAsRead(msg.id).then(updated => {
                        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, ...updated, content: m.content } : m));
                        if ((window as any).electronAPI) {
                            (window as any).electronAPI.saveMessage({ ...msg, ...updated, content: msg.content });
                        }
                    }).catch(console.error));
                }
            }
        });

        if (activeThread && activeThread.replies) {
            activeThread.replies.forEach(reply => {
                const isRead = reply.read_by?.some(r => r.user_id === currentUser.id);
                if (!isRead && reply.author_id !== currentUser.id) {
                    promises.push(api.markMessageAsRead(reply.id).then(updated => {
                        setActiveThread(prev => {
                            if (!prev || !prev.replies) return prev;
                            return {
                                ...prev,
                                replies: prev.replies.map(r => r.id === reply.id ? { ...r, ...updated, content: r.content } : r)
                            };
                        });
                        if ((window as any).electronAPI) {
                            (window as any).electronAPI.saveMessage({ ...reply, ...updated, content: reply.content });
                        }
                    }).catch(console.error));
                }
            });
        }

        if (promises.length > 0) {
            Promise.all(promises).then(() => {
                loadUnreadCounts();
            });
        }
    }, [messages, activeThread, activeView, settings?.enable_read_receipts, currentUser, isWindowFocused]);

    const hasPermission = useCallback((permission: string): boolean => {
        if (!currentUser || !currentUser.role_id) return false;
        const role = roles.find(r => r.id === currentUser.role_id);
        if (!role) return false;

        // Super Admin Bypass
        if (role.permissions && Array.isArray(role.permissions) && role.permissions.includes('super:admin')) return true;

        // Full Admin Checks
        if (role.permissions && Array.isArray(role.permissions) && role.permissions.includes('admin:full') && permission.startsWith('admin:')) return true;

        return role.permissions && Array.isArray(role.permissions) && role.permissions.includes(permission);
    }, [currentUser, roles]);

    const hasModuleAccess = useCallback((moduleId: ModuleId): boolean => {
        if (!currentUser || !currentUser.role_id) return false;
        const role = roles.find(r => r.id === currentUser.role_id);
        if (!role) return false;
        // Super Admin Bypass
        if (role.permissions && Array.isArray(role.permissions) && role.permissions.includes('super:admin')) return true;
        return (role.allowed_modules && Array.isArray(role.allowed_modules) && role.allowed_modules.includes(moduleId)) ?? false;
    }, [currentUser, roles]);

    const getAuthHeader = useCallback(() => {
        return { Authorization: `Bearer ${authToken}` };
    }, [authToken]);

    const isAdmin = hasPermission('admin:view');

    const value: AppContextType = {
        // Data
        currentUser,
        allUsers,
        users,
        departments,
        setDepartments,
        channels,
        directMessages,
        topics,
        tickets,
        projects,
        messages,
        scheduledMessages,
        cancelScheduledMessage,

        // UI State
        activeView,
        setActiveView,
        activeThread,
        isLoading,
        isMessagesLoading,
        editingMessageId,
        setEditingMessageId,
        editContent,
        setEditContent,

        // Modal State
        showCreateChannel,
        setShowCreateChannel,
        showCreateTicket,
        setShowCreateTicket,
        showCreateProject,
        setShowCreateProject,
        showCreateTopic,
        setShowCreateTopic,
        showCreateDM,
        setShowCreateDM,
        showSettings,
        setShowSettings,

        // Actions
        sendMessage,
        editMessage,
        deleteMessage,
        markMessageAsRead,
        toggleReaction,
        replyToThread,
        createChannel,
        updateChannel,
        createTicket,
        updateTicket,
        deleteTicket,
        createProject,
        updateProject,
        archiveProject,
        refreshProjects,
        moveProjectTicket,
        bulkUpdateProjectTickets,
        setTicketCustomField,
        createTopic,
        updateTopic,
        archiveTopic,
        unarchiveTopic,
        deleteTopic,
        archiveChannel,
        unarchiveChannel,
        deleteChannel,
        createDM,
        updateUser,
        createDepartment,
        updateDepartment,
        uploadFile,
        updateFileRetention,
        openThread,
        closeThread,
        loadMessages,

        // Helpers
        getActiveViewData,
        getUserById,
        getDepartmentById,
        hasPermission,
        hasModuleAccess,
        getAuthHeader,
        isAdmin,
        createRole,
        updateRole,
        deleteRole,
        updateSettings,
        updateStatus,
        requestNotificationPermission,
        sendNotification,
        markAsUnread,
        snoozeMessage,
        unreadCounts,
        loadUnreadCounts,
        roles,
        settings,
        authToken,
        isOnline,
        login,
        verify2FA,
        logout,

        // E2EE
        recoverKeys,
        myKeys,

        // Socket real-time setters
        _setMessages: setMessages,
        _setChannels: setChannels,
        _setDirectMessages: setDirectMessages,
        _setUsers: setAllUsers,
        _setTopics: setTopics,
        _setTickets: setTickets,
        _setProjects: setProjects,
        _decryptMessages: decryptMessages,
        _setActiveThread: setActiveThread
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
}
